import React, { useMemo, useEffect, useState } from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Autocomplete,
	CircularProgress,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { fetchAllShifts } from 'core/API/shifts';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import moment from 'moment-timezone';
import { getTimeOffReasons, TimeOffReason } from '../../../../../core/API/scheduler';
import { ShiftRequest } from 'types/shift-requests';
import { getCoverRequestFormValues } from '../../../utils/coverRequestForm';
import { mergePickupShiftOptions } from '../../../utils/pickupRequestForm';
import { findTimeOffReason } from '../../../utils/timeOffRequestForm';

interface CoverRequestFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	formRef?: React.MutableRefObject<any>;
	editRequest?: ShiftRequest | null;
}

const validationSchema = Yup.object({
	shiftId: Yup.string().required('Please select a shift to cover'),
});

const formatCoverShiftLabel = (shift: any) => {
	const start = shift.startAt || shift.start_at || shift.start;
	const end = shift.endAt || shift.end_at || shift.end;
	const startLabel = start ? moment(start).format('ddd, MMM D - h:mm A') : 'Shift';
	const endLabel = end ? moment(end).format('h:mm A') : '';
	const jobLabel = shift.jobCode?.label || 'Shift';
	return endLabel ? `${startLabel} - ${endLabel} | ${jobLabel}` : `${startLabel} | ${jobLabel}`;
};

const CoverRequestForm: React.FC<CoverRequestFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	currentWeekStart,
	currentWeekEnd,
	formRef,
	editRequest,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const currentUserId = localStorage.getItem('userId');
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [loadingReasons, setLoadingReasons] = useState(true);
	const selectedShiftId = getCoverRequestFormValues(editRequest).shiftId;

	useEffect(() => {
		const fetchTimeOffReasons = async () => {
			try {
				const reasons = await getTimeOffReasons();
				const activeReasons = reasons.filter((r) => r.active && !r.deleted);
				const selectedReason = findTimeOffReason(
					reasons,
					getCoverRequestFormValues(editRequest).reason
				);
				setTimeOffReasons(
					selectedReason && !activeReasons.some((reason) => reason.id === selectedReason.id)
						? [selectedReason, ...activeReasons]
						: activeReasons
				);
			} catch (error) {
				console.error('Failed to fetch time off reasons:', error);
			} finally {
				setLoadingReasons(false);
			}
		};

		fetchTimeOffReasons();
	}, [editRequest]);

	// Fetch user's shifts for cover request - only for current user and selected week
	const { data: shiftsData, isLoading: shiftsLoading } = useQuery(
		['user-shifts', workplaceId, currentUserId, currentWeekStart, currentWeekEnd],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
				toDate: currentWeekEnd ? moment(currentWeekEnd).add(1, 'day').format('YYYY-MM-DD') : undefined,
			}),
		{
			enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd && !!currentUserId,
			staleTime: 0,
			cacheTime: 0,
		}
	);

	// Filter user's shifts for the selected week
	const userShifts = useMemo(() => {
		if (!shiftsData?.member || !currentUserId) return [];
		const weekStart = moment(currentWeekStart).startOf('day');
		const weekEnd = moment(currentWeekEnd).endOf('day');
		const now = moment(); // Current time

		const filtered = shiftsData.member.filter((shift: any) => {
			if (selectedShiftId && String(shift.id) === selectedShiftId) return true;

			const employeeId =
				shift.employee?.id?.toString() || shift.employee?.toString().replace('/api/employees/', '');
			const shiftStart = moment(shift.startAt || shift.start);

			// Only include published shifts for current user that:
			// 1. Fall within the selected week
			// 2. Start after the current time (future shifts only)
			return (
				employeeId === currentUserId &&
				shiftStart.isBetween(weekStart, weekEnd, null, '[]') &&
				shift.published === true &&
				shiftStart.isAfter(now)
			);
		});

		// Sort by startAt first, then by jobCode label
		return filtered.sort((a: any, b: any) => {
			const startA = moment(a.startAt || a.start);
			const startB = moment(b.startAt || b.start);

			// Compare by start time first
			const startComparison = startA.diff(startB);
			if (startComparison !== 0) return startComparison;

			// If start times are equal, compare by job code label
			const labelA = a.jobCode?.label || '';
			const labelB = b.jobCode?.label || '';
			return labelA.localeCompare(labelB);
		});
	}, [shiftsData, currentUserId, currentWeekStart, currentWeekEnd, selectedShiftId]);

	const coverShiftOptions = useMemo(() => {
		const selectedFromWeek = userShifts.find((shift: any) => String(shift.id) === selectedShiftId);
		const selectedShiftFallback = selectedShiftId ? { id: Number(selectedShiftId) || selectedShiftId } : null;
		return mergePickupShiftOptions(
			userShifts,
			selectedFromWeek || editRequest?.shiftRequest || selectedShiftFallback,
			selectedShiftId
		);
	}, [userShifts, editRequest?.shiftRequest, selectedShiftId]);

	const initialValues = useMemo(
		() => getCoverRequestFormValues(editRequest),
		[editRequest]
	);

	const handleFormSubmit = async (formValues: ReturnType<typeof getCoverRequestFormValues>) => {
		const matchedReason = findTimeOffReason(timeOffReasons, formValues.reason);
		await onSubmit({
			...formValues,
			reasonDescription: formValues.reasonDescription || matchedReason?.description || '',
			reasonNote: formValues.reasonNote || matchedReason?.note || '',
		});
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			enableReinitialize
			validationSchema={validationSchema}
			onSubmit={handleFormSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => {
				const selectedReason = findTimeOffReason(timeOffReasons, values.reason);
				const reasonDescription =
					values.reasonDescription || selectedReason?.description || '';
				const reasonNote = values.reasonNote || selectedReason?.note || '';

				return (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							{editRequest ? 'Edit Cover Request' : 'Request Shift Coverage'}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Week: {currentWeekStart && moment(currentWeekStart).format('MMM D')} -{' '}
							{currentWeekEnd && moment(currentWeekEnd).format('MMM D, YYYY')}
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControl fullWidth error={touched.shiftId && !!errors.shiftId}>
									<InputLabel>Select Your Shift *</InputLabel>
									<Select
										value={values.shiftId}
										onChange={(e) => setFieldValue('shiftId', String(e.target.value))}
										label="Select Your Shift *"
									>
										{shiftsLoading && coverShiftOptions.length === 0 ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> &nbsp; &nbsp; Loading
												shifts...
											</MenuItem>
										) : coverShiftOptions.length === 0 ? (
											<MenuItem value="" disabled>
												No shifts available this week
											</MenuItem>
										) : (
											coverShiftOptions.map((shift: any) => (
												<MenuItem key={shift.id} value={String(shift.id)}>
													{formatCoverShiftLabel(shift)}
												</MenuItem>
											))
										)}
									</Select>
									{touched.shiftId && errors.shiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{errors.shiftId}
										</Typography>
									)}
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									options={timeOffReasons}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
									value={selectedReason}

									onChange={(_, value) => {
										setFieldValue('reason', value ? value['@id'] : '');
										setFieldValue('reasonDescription', value?.description || '');
										setFieldValue('reasonNote', value?.note || '');
									}}
									isOptionEqualToValue={(option, value) =>
										option['@id'] === value?.['@id'] || option.id === value?.id
									}
									loading={loadingReasons}
									disabled={loadingReasons}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Reason"
											fullWidth
										/>
									)}
								/>
								{values.reason && (
									<>
										{reasonDescription && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={reasonDescription}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Description"
											/>
										)}
										{reasonNote && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={reasonNote}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Note"
											/>
										)}
									</>
								)}
							</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={3}
								label="Notes (Optional)"
								value={values.notes}
								onChange={(e) => setFieldValue('notes', e.target.value)}
								placeholder="Add any additional notes or details"
							/>
						</Grid>
					</Grid>
				</Box>
				</Form>
				);
			}}
		</Formik>
	);
};

export default CoverRequestForm;